import { create } from 'zustand';
import { IssueType } from '@/types';

/**
 * Issue Type state store using Zustand
 * Manages issue type-related state for the project management app
 */

interface IssueTypeState {
  // Issue types for current workspace
  issueTypes: IssueType[];
  defaultIssueType: IssueType | null;

  // Loading state
  isLoading: boolean;
}

interface IssueTypeActions {
  // Issue type list actions
  setIssueTypes: (issueTypes: IssueType[]) => void;
  addIssueType: (issueType: IssueType) => void;
  updateIssueType: (issueTypeId: string, updates: Partial<IssueType>) => void;
  removeIssueType: (issueTypeId: string) => void;

  // Default issue type actions
  setDefaultIssueType: (issueType: IssueType | null) => void;

  // Reorder actions
  reorderIssueTypes: (orderedIds: string[]) => void;

  // Loading actions
  setLoading: (isLoading: boolean) => void;

  // Clear all issue type data
  clearIssueTypes: () => void;
}

type IssueTypeStore = IssueTypeState & IssueTypeActions;

export const useIssueTypeStore = create<IssueTypeStore>()((set) => ({
  // Initial state
  issueTypes: [],
  defaultIssueType: null,
  isLoading: false,

  // Issue type list actions
  setIssueTypes: (issueTypes: IssueType[]) => {
    set({ issueTypes, isLoading: false });
    // Also set default issue type
    const defaultType = issueTypes.find((it) => it.isDefault);
    if (defaultType) {
      set({ defaultIssueType: defaultType });
    }
  },

  addIssueType: (issueType: IssueType) => {
    set((state) => ({
      issueTypes: [...state.issueTypes, issueType].sort(
        (a, b) => a.displayOrder - b.displayOrder
      ),
    }));
  },

  updateIssueType: (issueTypeId: string, updates: Partial<IssueType>) => {
    set((state) => {
      const updatedIssueTypes = state.issueTypes.map((issueType) =>
        issueType.issueTypeId === issueTypeId
          ? { ...issueType, ...updates }
          : issueType
      );

      // Update default if this was set as default
      const newDefault = updates.isDefault
        ? updatedIssueTypes.find((it) => it.issueTypeId === issueTypeId)
        : state.defaultIssueType?.issueTypeId === issueTypeId
          ? { ...state.defaultIssueType, ...updates }
          : state.defaultIssueType;

      return {
        issueTypes: updatedIssueTypes,
        defaultIssueType: newDefault || null,
      };
    });
  },

  removeIssueType: (issueTypeId: string) => {
    set((state) => ({
      issueTypes: state.issueTypes.filter(
        (issueType) => issueType.issueTypeId !== issueTypeId
      ),
      defaultIssueType:
        state.defaultIssueType?.issueTypeId === issueTypeId
          ? null
          : state.defaultIssueType,
    }));
  },

  // Default issue type actions
  setDefaultIssueType: (issueType: IssueType | null) => {
    set((state) => {
      // Update isDefault flag on all issue types
      const updatedIssueTypes = state.issueTypes.map((it) => ({
        ...it,
        isDefault: issueType ? it.issueTypeId === issueType.issueTypeId : false,
      }));

      return {
        issueTypes: updatedIssueTypes,
        defaultIssueType: issueType,
      };
    });
  },

  // Reorder actions
  reorderIssueTypes: (orderedIds: string[]) => {
    set((state) => {
      const reorderedIssueTypes = orderedIds
        .map((id, index) => {
          const issueType = state.issueTypes.find((it) => it.issueTypeId === id);
          return issueType ? { ...issueType, displayOrder: index } : null;
        })
        .filter((it): it is IssueType => it !== null);

      return { issueTypes: reorderedIssueTypes };
    });
  },

  // Loading actions
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  // Clear all issue type data
  clearIssueTypes: () => {
    set({
      issueTypes: [],
      defaultIssueType: null,
      isLoading: false,
    });
  },
}));

/**
 * Selector hooks for specific state slices
 */
export const useIssueTypes = () => useIssueTypeStore((state) => state.issueTypes);
export const useDefaultIssueType = () =>
  useIssueTypeStore((state) => state.defaultIssueType);
export const useIssueTypeLoading = () =>
  useIssueTypeStore((state) => state.isLoading);

// Get issue type by ID
export const useIssueTypeById = (issueTypeId: string | undefined) =>
  useIssueTypeStore((state) =>
    issueTypeId
      ? state.issueTypes.find((it) => it.issueTypeId === issueTypeId)
      : undefined
  );

// Get issue types by hierarchy level
export const useIssueTypesByHierarchy = (level: number) =>
  useIssueTypeStore((state) =>
    state.issueTypes.filter((it) => it.hierarchyLevel === level)
  );

// Get epic issue types (hierarchy level 0)
export const useEpicIssueTypes = () =>
  useIssueTypeStore((state) =>
    state.issueTypes.filter((it) => it.hierarchyLevel === 0)
  );

// Get subtask issue types (hierarchy level 3)
export const useSubtaskIssueTypes = () =>
  useIssueTypeStore((state) =>
    state.issueTypes.filter((it) => it.hierarchyLevel === 3)
  );

export default useIssueTypeStore;
